import { StateObservable, combineEpics, ofType } from 'redux-observable';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, startWith } from 'rxjs/operators';
import { ConversationEvent, apiObservable, conversationObservable } from '@support/api';
import { ID, ReduxAction } from '@app/types';
import { requestActions } from '@actions/apiHelpers';
import { AppState } from '@reducers/rootReducer';
import { selectNexusStatus } from '@reducers/data/nexus';

/* epic actions */
const nexusServiceStatusActions = requestActions('NEXUS_SERVICE_STATUS', ['fetch']);
const nexusStatusActions = requestActions('NEXUS_STATUS', ['fetch']);
const nexusEnablementActions = requestActions('NEXUS_ENABLEMENT', ['update']);
const nexusSnapshotActions = requestActions('NEXUS_SNAPSHOT', ['update']);

/* component actions */
export const fetchNexusServiceStatus = nexusServiceStatusActions['fetch'];
export const fetchNexusStatus = nexusStatusActions['fetch'];
export const enableNexus = nexusEnablementActions['update'];
export const refreshNexus = nexusSnapshotActions['update'];

export const startConversation = (prompt: string, targetContactID?: ID): ReduxAction => {
  return {
    type: 'INITIATE_NEXUS_CONVERSATION',
    payload: { prompt, targetContactID },
  };
};

export const continueConversation = (
  conversationID: ID,
  prompt: string,
  targetContactID?: ID,
): ReduxAction => ({
  type: 'CONTINUE_NEXUS_CONVERSATION',
  payload: { conversationID, prompt, targetContactID },
});

export const regenerateLastResponse = (conversationID: ID, targetContactID?: ID): ReduxAction => ({
  type: 'REGENERATE_NEXUS_CONVERSATION_RESPONSE',
  payload: { conversationID, targetContactID, regenerate: true },
});

export const clearNewConversation: ReduxAction = {
  type: 'CLEAR_NEW_NEXUS_CONVERSATION',
};

export const deleteConversation = (conversationID: ID): ReduxAction => ({
  type: 'DELETE_NEXUS_CONVERSATION',
  payload: { conversationID },
});

const nexusConversationEventPayloadToAction = (payload: ConversationEvent): ReduxAction => {
  switch (payload.type) {
    case 'message':
      return { type: 'NEXUS_CONVERSATION_MSG_CHUNK_RECEIVED', payload };
    case 'contacts':
      return { type: 'NEXUS_CONVERSATION_MSG_CONTACTS_RECEIVED', payload };
    case 'conversation_contact':
      return { type: 'NEXUS_CONVERSATION_CONTACT_RECEIVED', payload };
    case 'close':
      return { type: 'NEXUS_CONVERSATION_MSG_TERMINATED', payload };
    case 'error':
      return { type: 'NEXUS_CONVERSATION_MSG_ERROR', payload };
    default:
      return undefined;
  }
};

// @ts-ignore
const nexusStatusJsonURL = NEXUS_STATUS_JSON_URL;
const nexusServiceStatusEpic = (action$: Observable<ReduxAction>): Observable<ReduxAction> =>
  action$.pipe(
    ofType('FETCH_NEXUS_SERVICE_STATUS'),
    mergeMap(({}) =>
      apiObservable(
        'GET',
        nexusStatusJsonURL,
        {},
        {
          noAuthorization: true,
          noTimeZone: true,
          timeStamp: true,
          isFullPath: true,
          headers: {
            Accept: '*/*',
          },
        },
      ).pipe(
        map(nexusServiceStatusActions['fetchSucceeded']),
        catchError((err) => of(nexusServiceStatusActions['fetchFailed'](err))),
      ),
    ),
  );

const nexusStatusEpic = (action$: Observable<ReduxAction>): Observable<ReduxAction> =>
  action$.pipe(
    ofType('FETCH_NEXUS_STATUS'),
    mergeMap(({}) =>
      apiObservable('GET', '/chat/status/', undefined, { apiVersion: 'v2' }).pipe(
        map(nexusStatusActions['fetchSucceeded']),
        startWith(nexusStatusActions['fetchRequested']()),
        catchError((err) => of(nexusStatusActions['fetchFailed'](err))),
      ),
    ),
  );

const nexusEnablementEpic = (action$: Observable<ReduxAction>): Observable<ReduxAction> =>
  action$.pipe(
    ofType('UPDATE_NEXUS_ENABLEMENT'),
    mergeMap(({ payload }) =>
      apiObservable('POST', '/chat/enable/', {}, { apiVersion: 'v2' }).pipe(
        map(() => nexusEnablementActions['updateSucceeded'](payload)),
        startWith(nexusEnablementActions['updateRequested']()),
        catchError((err) => of(nexusEnablementActions['updateFailed'](err))),
      ),
    ),
  );

const nexusRefreshEpic = (action$: Observable<ReduxAction>): Observable<ReduxAction> =>
  action$.pipe(
    ofType('UPDATE_NEXUS_SNAPSHOT'),
    mergeMap(({ payload }) =>
      apiObservable('POST', '/chat/snapshot/', {}, { apiVersion: 'v2' }).pipe(
        map(() => nexusSnapshotActions['updateSucceeded'](payload)),
        startWith(nexusSnapshotActions['updateRequested']()),
        catchError((err) => of(nexusSnapshotActions['updateFailed'](err))),
      ),
    ),
  );

const startConversationEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
): Observable<ReduxAction> =>
  action$.pipe(
    ofType('INITIATE_NEXUS_CONVERSATION'),
    mergeMap(({ payload }) => {
      const nexusDisabled = selectNexusStatus(state$.value).needs_permission;

      if (nexusDisabled) {
        return of({ type: 'NEXUS_CONVERSATION_NOT_ENABLED', payload });
      }

      return conversationObservable(payload).pipe(
        map((conversationEvent: ConversationEvent) => {
          if (conversationEvent.type === 'conversation') {
            return {
              type: 'INITIATE_NEXUS_CONVERSATION_SUCCEEDED',
              payload: conversationEvent,
            };
          }

          return nexusConversationEventPayloadToAction(conversationEvent);
        }),
        filter((response) => response !== undefined),
        catchError((error) => {
          console.log('error initating chat conversation', error);
          return of({ type: 'INITIATE_NEXUS_CONVERSATION_FAILED', payload: error });
        }),
      );
    }),
  );

const continueConversationEpic = (action$: Observable<ReduxAction>): Observable<ReduxAction> =>
  action$.pipe(
    ofType('CONTINUE_NEXUS_CONVERSATION', 'REGENERATE_NEXUS_CONVERSATION_RESPONSE'),
    mergeMap(({ payload }) =>
      conversationObservable(payload).pipe(
        map(nexusConversationEventPayloadToAction),
        filter((response) => response !== undefined),
        catchError((error) => of({ type: 'CONTINUE_NEXUS_CONVERSATION_FAILED', payload: error })),
      ),
    ),
  );

export const nexusEpic = combineEpics(
  nexusServiceStatusEpic,
  nexusStatusEpic,
  nexusEnablementEpic,
  nexusRefreshEpic,
  startConversationEpic,
  continueConversationEpic,
);
